import {
  BofuTabDocument,
  DocumentResponse,
  FORM_STATUS_ENUM,
  PersonaStructure,
  TabsConfigResponseData,
  TabsConfigResponseDocument,
} from "@interfaces/response";
import {
  DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE,
  PERSON_FORM_HUB_TYPE_CODES,
} from "@components/PersonForm/personFormConstants";
import {
  generateDocumentConfig,
  generatePropietarioDocs,
  validateDocumentStructure,
} from "@components/TabBofu/functions";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import { UUID } from "crypto";

export const formatPersonName = (person: PersonaStructure) =>
  `${person.name || ""} ${person.last_name || ""} ${person.last_name_2 || ""}`.trim() || "Sin nombre";

export const getPersonInitials = (person: PersonaStructure) => {
  const first = person.name?.trim()?.charAt(0) || "";
  const last = person.last_name?.trim()?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "?";
};

export const isPersonInfoComplete = (status: FORM_STATUS_ENUM | null | undefined) =>
  status === "COMPLETO" || status === "VALIDADO";

/** Posición del código en la jerarquía del hub; -1 si el hub no lo admite. */
const getFormHubTypeIndex = (code: string) => (PERSON_FORM_HUB_TYPE_CODES as readonly string[]).indexOf(code);

/**
 * Menor índice = mayor prioridad. Si tiene varios tipos, usa el de mayor jerarquía;
 * sin tipos del hub regresa `Infinity` para quedar al final.
 */
const getPersonFormHubSortRank = (person: PersonaStructure) => {
  let best = Number.POSITIVE_INFINITY;

  for (const personType of person.person_types_detail || []) {
    const index = getFormHubTypeIndex(personType.code);
    if (index !== -1 && index < best) {
      best = index;
    }
  }

  return best;
};

/** Tipos de la persona que admite el hub TBPER (`PERSON_FORM_HUB_TYPE_CODES`), en su orden original. */
export const getPersonOnlyPersonTypesAllowedToFormHub = (person: PersonaStructure) =>
  (person.person_types_detail || []).filter((personType) => getFormHubTypeIndex(personType.code) >= 0);

/**
 * Deja las personas con algún tipo del hub, con `person_types_detail` reducido a esos tipos,
 * ordenadas por jerarquía. Regresa copias porque los objetos de RTK Query están congelados.
 */
export const filterPersonsForFormHub = (persons: PersonaStructure[]) =>
  persons
    .map((person) => ({ ...person, person_types_detail: getPersonOnlyPersonTypesAllowedToFormHub(person) }))
    .filter((person) => person.person_types_detail.length > 0)
    .sort((a, b) => getPersonFormHubSortRank(a) - getPersonFormHubSortRank(b));

/**
 * Tipos de la persona que piden documentos en TBPER (los definidos en
 * `DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE`). Los tipos sin documentos se descartan.
 */
export const getDocumentPersonTypes = (person: PersonaStructure) =>
  (person.person_types_detail || []).filter((personType) =>
    Object.hasOwn(DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE, personType.code),
  );

/** Busca la configuración del tab TBPER dentro de la respuesta de `tabs_bofu_config`; `undefined` si no viene. */
export const findPersonasTab = (tabsConfig: TabsConfigResponseData[] | undefined) =>
  (tabsConfig || []).find((tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_personas.code);

/**
 * Arma los documentos de una persona según sus tipos, ordenados por `order`, y les asigna
 * los archivos ya cargados de esa persona (mismo file code y `person_id`).
 */
export const buildPersonDocuments = ({
  person,
  tabDocuments,
  uploadedDocuments,
  clientDetailsId,
}: {
  person: PersonaStructure;
  tabDocuments: TabsConfigResponseDocument[];
  uploadedDocuments: DocumentResponse[];
  clientDetailsId: UUID;
}): BofuTabDocument[] => {
  const relationships = new Set(
    getDocumentPersonTypes(person).flatMap((personType) => DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE[personType.code]),
  );

  // se descartan los documentos inválidos o solo de BPM y los de relaciones que no corresponden a sus tipos
  // (client_details, garantías u otros tipos de persona)
  const documents = tabDocuments
    .filter(
      (document) =>
        validateDocumentStructure(document) &&
        document.relationship !== undefined &&
        relationships.has(document.relationship),
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((docItem) => generateDocumentConfig({ docItem, docs_aval: [], clientDetailsId: undefined, aval: undefined }));

  return generatePropietarioDocs({
    personItem: person,
    valid_documents: documents,
    documents_uploaded: uploadedDocuments,
    clientDetailsId,
  });
};

export type PersonDocumentChange = {
  document: BofuTabDocument;
  newValue: DocumentResponse | undefined;
  valueAction?: "add" | "remove" | "update";
  valueIndex?: number;
};

/** Identifica el mismo documento por file code y relación, para no mezclar tipos de la misma persona. */
const isSameDocumentSlot = (current: BofuTabDocument, target: BofuTabDocument) =>
  current.file_category_code === target.file_category_code && current.relationship === target.relationship;

/**
 * Aplica un cambio sobre los documentos locales de la persona, sin escribir en el store de BOFU.
 * Documento simple: reemplaza `file_value`. Múltiple: agrega, actualiza o quita en `list_values` según `valueAction`.
 * Regresa un arreglo nuevo; los documentos sin cambios conservan su referencia.
 */
export const applyPersonDocumentChange = (
  documents: BofuTabDocument[],
  change: PersonDocumentChange,
): BofuTabDocument[] =>
  documents.map((document) => {
    if (!isSameDocumentSlot(document, change.document)) return document;

    if (!change.document.multiple) {
      return { ...document, file_value: change.newValue };
    }

    const listValues = [...document.list_values];

    if (change.valueAction === "add") {
      listValues.push(change.newValue);
    } else if (change.valueAction === "update" && change.valueIndex !== undefined) {
      listValues[change.valueIndex] = change.newValue;
    } else if (change.valueAction === "remove" && change.valueIndex !== undefined) {
      listValues.splice(change.valueIndex, 1);
    }

    return { ...document, list_values: listValues };
  });
